import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { SlidersHorizontal } from "lucide-react"

import { cn } from "@/lib/utils"
import { SearchField } from "@/components/ui/search-field"
import { ChipSelect } from "@/components/ui/chip-select"
import { FilterChip } from "@/components/ui/filter-chip"
import { SavedViews } from "@/components/ui/saved-views"

/*
  FilterBar — one toolbar for list pages: search · filter chips · "+ More filters" · "Clear all" ·
  (summary + actions on the right), with an optional saved-views row above it.

    const [f, setF, { reset }] = useUrlState({ q: '', system: [], engineer: [], city: [], tag: [], hasNotes: false });
    <FilterBar
      search={{ value: f.q, onChange: (q) => setF({ q }), placeholder: 'Search name, client, engineer, tag…', label: 'Search projects' }}
      filters={[
        { key: 'system', label: 'System', options: systemOptions, value: f.system, onChange: (system) => setF({ system }) },
        { key: 'engineer', label: 'Engineer', options: engineerOptions, value: f.engineer, onChange: (engineer) => setF({ engineer }) },
        { key: 'tag', label: 'Tags', options: tagOptions, value: f.tag, onChange: (tag) => setF({ tag }), more: true },
        { key: 'hasNotes', label: 'Has notes', type: 'toggle', value: f.hasNotes, onChange: (hasNotes) => setF({ hasNotes }), more: true },
      ]}
      onClearAll={() => reset()}
      views={views}                                  // useSavedViews(…) result → row above
      summary={<><b>52</b> of 735</>}
      actions={<ColumnsMenu state={columnState} />}
    />

  Filter: { key, label, options [{ value, label, count?, muted? }], value, onChange, multiple (default
  true → value is an array), type ('select' | 'toggle'), more (behind "More filters" until it has a
  value), icon, searchable, emptyText, popoverClassName, valueLabel (toggle text when on, 'Yes'), testId }.
  Multi-select popovers show option counts and a search box above 8 options.
  "Clear all" shows while any filter or the search is set; without `onClearAll` it clears each filter.
  `clearable` overrides when it shows — e.g. a Status filter that defaults to ['Active']:
  onClearAll={reset} clearable={!isDefault} (useUrlState), so the default view shows no "Clear all".
  Props: search ({ value, onChange, placeholder, label, testId, className }), filters, onClearAll,
  clearable, views, summary, actions, moreLabel ('More filters'), moreCount (true: "More filters 4" — how
  many filters wait behind it), phoneScroll (below 640px the chips stay on one row that scrolls sideways
  under the full-width search, as in the phone mockups; default false = they wrap — the chips, More filters and
  Clear all are then 36px tall on phones), className, testId.
*/

// phoneScroll: phone chips are 36px tall (mobile spec), 30px from 640px up.
const PHONE_CHIP = "max-sm:h-9"

function isFilterActive(filter) {
  if (filter.type === "toggle") return Boolean(filter.value)
  if (Array.isArray(filter.value)) return filter.value.length > 0
  return filter.value !== null && filter.value !== undefined && filter.value !== ""
}

function emptyValue(filter) {
  if (filter.type === "toggle") return false
  return filter.multiple === false ? null : []
}

function FilterControl({ filter, autoOpen, chipClassName }) {
  if (filter.type === "toggle") {
    const on = Boolean(filter.value)
    return (
      <FilterChip
        label={filter.label}
        icon={filter.icon}
        value={on ? (filter.valueLabel || "Yes") : null}
        aria-pressed={on}
        onClick={() => filter.onChange(!on)}
        onClear={on ? () => filter.onChange(false) : undefined}
        className={chipClassName}
        data-testid={filter.testId}
      />
    )
  }
  return (
    <ChipSelect
      label={filter.label}
      icon={filter.icon}
      multiple={filter.multiple !== false}
      value={filter.value}
      options={filter.options || []}
      onChange={filter.onChange}
      searchable={filter.searchable}
      emptyText={filter.emptyText}
      popoverClassName={filter.popoverClassName}
      allLabel={filter.allLabel}
      footer={filter.footer}
      defaultOpen={autoOpen}
      className={chipClassName}
      testId={filter.testId}
    />
  )
}

function FilterBar({
  search,
  filters = [],
  onClearAll,
  clearable,
  views,
  summary,
  actions,
  moreLabel = "More filters",
  moreCount = true,
  phoneScroll = false,
  className,
  testId = "filter-bar",
}) {
  const [revealed, setRevealed] = React.useState(() => new Set())
  const [justRevealed, setJustRevealed] = React.useState(null)
  const visibleFilters = filters.filter((filter) => filter && !filter.hidden)
  const shown = visibleFilters.filter((filter) => !filter.more || isFilterActive(filter) || revealed.has(filter.key))
  const hiddenMore = visibleFilters.filter((filter) => filter.more && !shown.includes(filter))
  const searchActive = Boolean(search && search.value)
  const anyActive = searchActive || visibleFilters.some(isFilterActive)
  const showClear = clearable ?? anyActive

  const clearAll = () => {
    if (onClearAll) onClearAll()
    else {
      visibleFilters.filter(isFilterActive).forEach((filter) => filter.onChange(emptyValue(filter)))
      if (searchActive) search.onChange("")
    }
    setRevealed(new Set())
  }

  const reveal = (key) => {
    setRevealed((current) => new Set(current).add(key))
    setJustRevealed(key)
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-2.5", className)} data-testid={testId}>
      {views ? <SavedViews views={views} testId={`${testId}-views`} /> : null}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {search ? (
          <SearchField
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder}
            label={search.label}
            testId={search.testId}
            shortcut={search.shortcut}
            className={cn("w-full sm:w-[300px]", search.className)}
          />
        ) : null}
        <div
          className={cn(
            "contents",
            phoneScroll && "max-sm:-mx-4 max-sm:flex max-sm:w-[calc(100%+2rem)] max-sm:min-w-0 max-sm:items-center max-sm:gap-2 max-sm:overflow-x-auto max-sm:px-4 max-sm:[scrollbar-width:none] max-sm:[&::-webkit-scrollbar]:hidden max-sm:[&>*]:shrink-0"
          )}
          data-testid={`${testId}-chips`}
        >
        {shown.map((filter) => (
          <FilterControl key={filter.key} filter={filter} autoOpen={justRevealed === filter.key} chipClassName={phoneScroll ? PHONE_CHIP : undefined} />
        ))}
        {hiddenMore.length ? (
          <DropdownMenuPrimitive.Root modal={false}>
            <DropdownMenuPrimitive.Trigger asChild>
              <FilterChip
                label={moreCount ? (
                  <>
                    {moreLabel}
                    <span className="ml-1.5 rounded-full bg-[color:var(--status-gray-bg)] px-1.5 py-px text-[11px] font-bold tabular-nums text-ink-2">
                      {hiddenMore.length}
                    </span>
                  </>
                ) : moreLabel}
                icon={SlidersHorizontal}
                className={phoneScroll ? PHONE_CHIP : undefined}
                data-testid={`${testId}-more`}
              />
            </DropdownMenuPrimitive.Trigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.Content
                align="start"
                sideOffset={4}
                collisionPadding={8}
                // The picked filter's own popover opens next; do not pull focus back to this button.
                onCloseAutoFocus={(event) => event.preventDefault()}
                className="z-50 min-w-[200px] rounded-xl border border-line bg-surface p-1.5 text-ink shadow-overlay"
              >
                <DropdownMenuPrimitive.Label className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
                  Add a filter
                </DropdownMenuPrimitive.Label>
                {hiddenMore.map((filter) => (
                  <DropdownMenuPrimitive.Item
                    key={filter.key}
                    onSelect={() => {
                      if (filter.type === "toggle") filter.onChange(true)
                      else setTimeout(() => reveal(filter.key), 0)
                    }}
                    className="flex h-8 cursor-default select-none items-center gap-2 rounded-lg px-2.5 text-[13px] outline-none data-[highlighted]:bg-surface-hover"
                  >
                    {filter.icon ? <filter.icon className="h-[15px] w-[15px] text-ink-3" aria-hidden="true" /> : null}
                    {filter.label}
                  </DropdownMenuPrimitive.Item>
                ))}
              </DropdownMenuPrimitive.Content>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Root>
        ) : null}
        {showClear ? (
          <button
            type="button"
            onClick={clearAll}
            className={cn("h-[30px] rounded-full px-2.5 text-[12.5px] font-semibold text-brand-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", phoneScroll && PHONE_CHIP)}
            data-testid={`${testId}-clear`}
          >
            Clear all
          </button>
        ) : null}
        </div>
        {summary || actions ? (
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1">
            {summary ? <span className="whitespace-nowrap text-[12.5px] text-ink-3 [&_b]:font-semibold [&_b]:text-ink-2" aria-live="polite">{summary}</span> : null}
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export { FilterBar, isFilterActive }
